import { cn } from '@/lib/utils';
import type { TicketCategory, TicketStatus } from '@/types/ticket';
import {
  TICKET_CATEGORY_LABELS,
  TICKET_STATUS_LABELS,
  studentInitials,
  ticketCategoryChipClass,
  ticketStatusChipClass,
} from '@/components/tickets/ticketLabels';

type TicketChatHeaderProps = {
  title: string;
  ticketId: string;
  category: TicketCategory;
  status: TicketStatus;
  studentName?: string;
  deletedByStudent?: boolean;
  onMarkInProcess: () => void;
  onClose: () => void;
  onRestore?: () => void;
};

function StatusChip({ status }: { status: TicketStatus }) {
  const { chip, dot } = ticketStatusChipClass(status);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold whitespace-nowrap',
        chip
      )}
    >
      <span className={cn('inline-block h-2 w-2 shrink-0 rounded-full', dot)} aria-hidden />
      {TICKET_STATUS_LABELS[status] ?? status}
    </span>
  );
}

export function TicketChatHeader({
  title,
  ticketId,
  category,
  status,
  studentName,
  deletedByStudent,
  onMarkInProcess,
  onClose,
  onRestore,
}: TicketChatHeaderProps) {
  const initials = studentInitials(studentName);

  return (
    <div className="flex items-start gap-4 border-b bg-background px-5 py-4 shrink-0">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div
          className="grid h-[46px] w-[46px] shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary-from to-primary-to text-sm font-bold text-primary-foreground"
          aria-hidden
        >
          {initials}
        </div>
        <div className="min-w-0">
          <h2 className="line-clamp-2 text-lg leading-snug font-bold tracking-tight">{title}</h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span
              className={cn(
                'inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold whitespace-nowrap',
                ticketCategoryChipClass()
              )}
            >
              {TICKET_CATEGORY_LABELS[category] ?? category}
            </span>
            <StatusChip status={status} />
            <span className="text-[11px] font-semibold text-foreground">{ticketId}</span>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2 self-center">
        {status === 'just_created' && (
          <button
            type="button"
            onClick={onMarkInProcess}
            className="h-[38px] rounded-full bg-primary-gradient px-3.5 text-xs font-bold text-primary-foreground"
          >
            Mark In Process
          </button>
        )}
        {deletedByStudent && onRestore && (
          <button
            type="button"
            onClick={onRestore}
            className="h-[38px] rounded-full bg-primary-gradient px-3.5 text-xs font-bold text-primary-foreground"
          >
            Restore Ticket
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          disabled={status === 'closed'}
          className="h-[38px] rounded-full border border-destructive/30 bg-background px-3.5 text-xs font-bold text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Close Ticket
        </button>
      </div>
    </div>
  );
}
